import * as React from "react";
import { useLocation, useRouter } from "@tanstack/react-router";
import { AddressDisplayProvider, QuickActionsProvider, useLoadBudget } from "@authe/ui-core";
import { usePermissions } from "@authe/ui-core/permissions";
import { RESOURCES } from "@/config";
import { useStorageIdentity } from "@/app/storageIdentity";
import type { FocusTarget } from "@authe/fe-commons/focus";
import { overlayRefKey, type OverlayRef } from "@authe/fe-commons/overlays";
import { useCatalogApi } from "@/api/adapters";
import { useSubjectHrefs } from "@/features/inspector/useSubjectHrefs";
import { useSubjectOpener } from "@/features/inspector/useSubjectOpener";
import { useResolvedAddressOverlaysBatch } from "./useResolvedAddressOverlaysBatch";
import { useQuickActionsPref } from "./useQuickActionsPref";
import { addressChecksum } from "./addressChecksum";

const AddressCatalogDialog = React.lazy(() => import("@/features/addresses/AddressCatalogDialog"));

/** Shared address defaults for routed views, including panels rendered in portals. */
export function DashboardAddressDisplayProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const router = useRouter();
  const onNavigateContract = React.useCallback((href: string) => { void router.navigate({ href }); }, [router]);
  const pathname = useLocation({ select: location => location.pathname });
  const identityKey = JSON.stringify(useStorageIdentity());
  const { can } = usePermissions();
  const canEdit = can(RESOURCES.addresses, "edit");
  const [catalogTarget, setCatalogTarget] = React.useState<{ address: string; chain?: string; trigger: HTMLElement | null } | null>(null);
  // Navigation, workspace changes and permission loss dismiss the old editor.
  React.useEffect(() => setCatalogTarget(null), [pathname, canEdit, identityKey]);
  const editCatalog = React.useCallback((target: FocusTarget) => {
    if (!canEdit || target.kind !== "address") return;
    setCatalogTarget({ address: target.id, chain: target.chain || undefined,
      trigger: document.activeElement instanceof HTMLElement ? document.activeElement : null });
  }, [canEdit]);
  const { addressHref, contractHref } = useSubjectHrefs();
  const { openAddress } = useSubjectOpener();
  const catalog = useCatalogApi();
  const budget = useLoadBudget("catalog.enrichment");
  const mounted = React.useRef(new Map<string, { ref: OverlayRef; count: number }>());
  const [refs, setRefs] = React.useState<OverlayRef[]>([]);
  const queued = React.useRef(false);
  const live = React.useRef(true);
  React.useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const flush = React.useCallback(() => {
    if (queued.current) return;
    queued.current = true;
    queueMicrotask(() => {
      queued.current = false;
      if (!live.current) return;
      const next = [...mounted.current.values()].map(entry => entry.ref);
      setRefs(previous => previous.length === next.length && previous.every((ref, i) =>
        overlayRefKey(ref.chainId, ref.address) === overlayRefKey(next[i]!.chainId, next[i]!.address)) ? previous : next);
    });
  }, []);
  const registerAddress = React.useCallback((address: string, chainId?: string) => {
    const key = overlayRefKey(chainId, address);
    const current = mounted.current.get(key);
    if (current) current.count += 1;
    else mounted.current.set(key, { ref: { address, chainId: chainId ?? null }, count: 1 });
    flush();
    return () => {
      const entry = mounted.current.get(key);
      if (entry && --entry.count === 0) mounted.current.delete(key);
      flush();
    };
  }, [flush]);
  // Reuse the identity/scoped per-address cache and coalesced transport. This
  // also shares answers with tables and graphs that already requested a ref.
  const overlays = useResolvedAddressOverlaysBatch(refs, {
    includeRow: true,
    maxRefs: budget.enrichment,
    resolveLogoUrl: catalog.resolveLogoUrl,
  });
  const onDrilldown = React.useCallback((target: FocusTarget) => {
    if (target.kind !== "address") return;
    openAddress(target.id, target.chain || undefined);
  }, [openAddress]);
  // An address can appear inside a sentence, a table cell or an existing
  // button. Keep the default unboxed and follow the surrounding typography;
  // identity headers and standalone chips select their own presentation.
  // The reader's own "when are the icons beside an address drawn" — above every
  // surface's default, so a page wrapping itself in `actions="always"` cannot
  // undo it (Settings › General).
  const [quickActions] = useQuickActionsPref();
  return <AddressDisplayProvider checksumFormatter={addressChecksum} presentation="inline" size="inherit" showExplorerIcon getInternalHref={addressHref} getContractHref={contractHref} onNavigateContract={onNavigateContract}
    onDrilldown={onDrilldown} onEditCatalog={canEdit ? editCatalog : null} resolveOverlay={overlays.resolveOverlay} registerAddress={registerAddress}>
    <QuickActionsProvider {...(quickActions ? { userActions: quickActions } : {})}>
      {children}
    </QuickActionsProvider>
    {catalogTarget && canEdit && <React.Suspense fallback={null}>
      <AddressCatalogDialog key={`${catalogTarget.chain ?? ""}:${catalogTarget.address}`}
        {...catalogTarget} onClose={() => setCatalogTarget(null)} />
    </React.Suspense>}
  </AddressDisplayProvider>;
}
